/**
 * HSE / safety editable day panel — the day's HSE header for a single Parte
 * Diario (scoped to `reportId`), with an EDIT trigger (PUT, keyed on entity_id)
 * and, when the day has no header yet, an ADD trigger (POST). Both open a
 * stylized `<Modal>` (the house rule: edits open in a modal, not inline).
 * Oxbo-P owns the record; this panel only submits the as-entered values and
 * refetches what P serves.
 *
 * dm_safety is a per-day header (not a per-observation incident row), so there
 * is at most ONE safety row per report — we edit that one, or add it. The coded
 * fields here are numeric/boolean, so booleans stay as `.brand-check` toggles.
 */
import { useEffect, useState } from "react";
import { useLocale } from "@hooks/useLocale";
import { QueryState } from "@components/ui/QueryState";
import { Modal } from "@components/ui/Modal";
import { useGetEventSafetyQuery } from "@api/pApi";
import {
  useCreateSafetyMutation,
  useUpdateSafetyMutation,
  type SafetyRowOut,
  type SafetyWrite,
} from "./hseApi";

/** Was this mutation rejected for lack of a write role (403)? */
function isForbidden(err: unknown): boolean {
  return (
    !!err &&
    typeof err === "object" &&
    "status" in err &&
    (err as { status: unknown }).status === 403
  );
}

/** The editable-header form state — strings so empty inputs stay controlled. */
interface FormState {
  date_report: string;
  days_since_lti_operator: string;
  days_since_lti_total: string;
  rid_cards: string;
  job_safety_analysis: string;
  jobsite_evaluation: string;
  bop_pressure_annular: string;
  last_casing_test_pressure: string;
  is_company_inspected: boolean;
  is_government_inspected: boolean;
}

const EMPTY_FORM: FormState = {
  date_report: "",
  days_since_lti_operator: "",
  days_since_lti_total: "",
  rid_cards: "",
  job_safety_analysis: "",
  jobsite_evaluation: "",
  bop_pressure_annular: "",
  last_casing_test_pressure: "",
  is_company_inspected: false,
  is_government_inspected: false,
};

/** Seed the form from a served row (numbers → strings; nulls → ""). */
function formFromRow(row: SafetyRowOut): FormState {
  const s = (v: number | null): string => (v == null ? "" : String(v));
  return {
    date_report: row.date_report ?? "",
    days_since_lti_operator: s(row.days_since_lti_operator),
    days_since_lti_total: s(row.days_since_lti_total),
    rid_cards: s(row.rid_cards),
    job_safety_analysis: s(row.job_safety_analysis),
    jobsite_evaluation: s(row.jobsite_evaluation),
    bop_pressure_annular: s(row.bop_pressure_annular),
    last_casing_test_pressure: s(row.last_casing_test_pressure),
    is_company_inspected: row.is_company_inspected ?? false,
    is_government_inspected: row.is_government_inspected ?? false,
  };
}

/** Trimmed empty → null; otherwise the parsed number (NaN → null). */
function numOrNull(v: string): number | null {
  const t = v.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** Build the wire body from the form (only send a date when one is entered). */
function bodyFromForm(f: FormState): SafetyWrite {
  return {
    date_report: f.date_report.trim() === "" ? null : f.date_report,
    days_since_lti_operator: numOrNull(f.days_since_lti_operator),
    days_since_lti_total: numOrNull(f.days_since_lti_total),
    rid_cards: numOrNull(f.rid_cards),
    job_safety_analysis: numOrNull(f.job_safety_analysis),
    jobsite_evaluation: numOrNull(f.jobsite_evaluation),
    bop_pressure_annular: numOrNull(f.bop_pressure_annular),
    last_casing_test_pressure: numOrNull(f.last_casing_test_pressure),
    is_company_inspected: f.is_company_inspected,
    is_government_inspected: f.is_government_inspected,
  };
}

export function HseEditPanel({ reportId, eventId }: { reportId: string; eventId: string }) {
  const { t } = useLocale();
  const { data, isLoading, isError, refetch } = useGetEventSafetyQuery(eventId, {
    skip: !eventId,
  });

  // At most one HSE header per report. The list is typed as the pre-write
  // `SafetyOut`; the live wire carries entity_id/version too, so read it as the
  // richer local shape for the PUT key.
  const dayRow = ((data?.items ?? []) as unknown as SafetyRowOut[]).find(
    (s) => s.daily_report_id === reportId,
  );

  return (
    <QueryState isLoading={isLoading} isError={isError} onRetry={refetch}>
      <div className="card">
        <h3>{t("hse.edit.title")}</h3>
        {dayRow ? (
          <SafetySummary key={dayRow.entity_id} reportId={reportId} row={dayRow} onSaved={refetch} />
        ) : (
          <AddSafety reportId={reportId} onSaved={refetch} />
        )}
      </div>
    </QueryState>
  );
}

/** The day's existing header: a compact read summary with an Editar trigger. */
function SafetySummary({
  reportId,
  row,
  onSaved,
}: {
  reportId: string;
  row: SafetyRowOut;
  onSaved: () => void;
}) {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const kv = (labelKey: Parameters<typeof t>[0], v: number | string | boolean | null) => (
    <div className="kv">
      <span className="kv-k">{t(labelKey)}</span>
      <span className="kv-v">
        {v == null || v === "" ? "—" : typeof v === "boolean" ? (v ? t("tub.yes") : t("tub.no")) : v}
      </span>
    </div>
  );

  return (
    <div>
      <div className="kv-grid" style={{ marginTop: 8 }}>
        {kv("hse.edit.date", row.date_report)}
        {kv("hse.edit.ltiOperator", row.days_since_lti_operator)}
        {kv("hse.edit.ltiTotal", row.days_since_lti_total)}
        {kv("hse.edit.ridCards", row.rid_cards)}
        {kv("hse.edit.jsa", row.job_safety_analysis)}
        {kv("hse.edit.jobsiteEval", row.jobsite_evaluation)}
        {kv("hse.edit.bopAnnular", row.bop_pressure_annular)}
        {kv("hse.edit.casingTest", row.last_casing_test_pressure)}
        {kv("hse.edit.company", row.is_company_inspected ?? false)}
        {kv("hse.edit.government", row.is_government_inspected ?? false)}
      </div>
      <div className="pick-row-actions" style={{ marginTop: 14 }}>
        <button type="button" className="btn primary" onClick={() => setOpen(true)}>
          {t("hse.edit.save")}
        </button>
      </div>
      {open ? (
        <SafetyModal
          mode="edit"
          reportId={reportId}
          entityId={row.entity_id}
          initial={formFromRow(row)}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false);
            onSaved();
          }}
        />
      ) : null}
    </div>
  );
}

/** The empty-day affordance: a "+ Agregar" trigger that opens the ADD modal. */
function AddSafety({ reportId, onSaved }: { reportId: string; onSaved: () => void }) {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  return (
    <div>
      <p className="subtitle" style={{ marginBottom: 12 }}>
        {t("hse.edit.none")}
      </p>
      <button type="button" className="btn primary" onClick={() => setOpen(true)}>
        {t("hse.edit.add")}
      </button>
      {open ? (
        <SafetyModal
          mode="add"
          reportId={reportId}
          initial={EMPTY_FORM}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false);
            onSaved();
          }}
        />
      ) : null}
    </div>
  );
}

/** The HSE header form inside a `<Modal>` — shared by ADD (POST) and EDIT (PUT). */
function SafetyModal({
  mode,
  reportId,
  entityId,
  initial,
  onClose,
  onSaved,
}: {
  mode: "add" | "edit";
  reportId: string;
  entityId?: string;
  initial: FormState;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useLocale();
  const [form, setForm] = useState<FormState>(initial);
  const [createSafety, createState] = useCreateSafetyMutation();
  const [updateSafety, updateState] = useUpdateSafetyMutation();
  const isSaving = createState.isLoading || updateState.isLoading;
  const error = createState.error ?? updateState.error;

  // Re-seed when the served row changes underneath an open edit form.
  useEffect(() => setForm(initial), [initial]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const num = (k: keyof FormState, labelKey: Parameters<typeof t>[0]) => (
    <label>
      <span>{t(labelKey)}</span>
      <input
        id={`hse-${k}`}
        type="number"
        inputMode="decimal"
        value={form[k] as string}
        onChange={(e) => set(k, e.target.value as FormState[typeof k])}
      />
    </label>
  );

  const check = (
    k: "is_company_inspected" | "is_government_inspected",
    labelKey: Parameters<typeof t>[0],
  ) => (
    <label className="field events-toggle" htmlFor={`hse-${k}`}>
      <span>{t(labelKey)}</span>
      <input
        id={`hse-${k}`}
        className="brand-check"
        type="checkbox"
        checked={form[k]}
        onChange={(e) => set(k, e.target.checked)}
      />
    </label>
  );

  const submit = async () => {
    const body = bodyFromForm(form);
    try {
      if (mode === "add") {
        await createSafety({ reportId, body }).unwrap();
      } else if (entityId) {
        await updateSafety({ reportId, entityId, body }).unwrap();
      }
      onSaved();
    } catch {
      // Surface below via `error`; RTK keeps it on the mutation state.
    }
  };

  return (
    <Modal
      open
      title={mode === "add" ? t("hse.edit.add") : t("hse.edit.title")}
      onClose={onClose}
      onConfirm={submit}
      busy={isSaving}
      confirmLabel={
        isSaving ? t("hse.edit.saving") : mode === "add" ? t("hse.edit.create") : t("hse.edit.save")
      }
      cancelLabel={t("hse.edit.cancel")}
      footerNote={error ? (isForbidden(error) ? t("hse.edit.forbidden") : t("hse.edit.error")) : null}
    >
      <div className="modal-grid">
        <label className="modal-span-all">
          <span>{t("hse.edit.date")}</span>
          <input
            id="hse-date_report"
            type="date"
            value={form.date_report}
            onChange={(e) => set("date_report", e.target.value)}
          />
        </label>
        {num("days_since_lti_operator", "hse.edit.ltiOperator")}
        {num("days_since_lti_total", "hse.edit.ltiTotal")}
        {num("rid_cards", "hse.edit.ridCards")}
        {num("job_safety_analysis", "hse.edit.jsa")}
        {num("jobsite_evaluation", "hse.edit.jobsiteEval")}
        {num("bop_pressure_annular", "hse.edit.bopAnnular")}
        {num("last_casing_test_pressure", "hse.edit.casingTest")}
        {check("is_company_inspected", "hse.edit.company")}
        {check("is_government_inspected", "hse.edit.government")}
      </div>
    </Modal>
  );
}
